// =============================================================================
// LIL & CO — Services Supabase : Rétention email (super admin)
// =============================================================================
// Résilient : renvoie ok/erreur, ne jette jamais.
//   • `retention_email_log` (table, RLS super_admin) : journal des envois ;
//   • `customer_tenants`     (RLS)                    : compteur désabonnements ;
//   • `send-retention-email` (Edge Function, JWT super_admin) : lance une vague
//      (dry_run pour l'aperçu, sinon envoi réel non-promotionnel + désinscription).
// =============================================================================
import { supabase } from './supabase';
import type { RetentionLogRow, RetentionAudience } from './retentionEmail';

export interface LoadRetentionResult {
  ok: boolean;
  rows: RetentionLogRow[];
  error?: string;
}

/** Journal des envois (le plus récent d'abord). `limit` borne le volume rapatrié. */
export async function loadRetentionLog(limit = 1000): Promise<LoadRetentionResult> {
  const { data, error } = await supabase
    .from('retention_email_log')
    .select('id,tenant_id,customer_id,email,variant_key,status,error_message,sent_at')
    .order('sent_at', { ascending: false })
    .limit(limit);
  if (error) return { ok: false, rows: [], error: error.message };
  return { ok: true, rows: (data ?? []) as RetentionLogRow[] };
}

/** Nombre de désabonnements (marketing) tous restos confondus. -1 si inaccessible. */
export async function loadUnsubCount(): Promise<number> {
  const { count, error } = await supabase
    .from('customer_tenants')
    .select('*', { count: 'exact', head: true })
    .not('unsubscribed_at', 'is', null);
  if (error) return -1;
  return count ?? 0;
}

export interface WaveResult {
  ok: boolean;
  dryRun: boolean;
  toSend?: number;
  candidates?: number;
  excludedRecent?: number;
  sent?: number;
  errors?: number;
  message: string;
}

/**
 * Déclenche (ou simule) une vague de rappels pour un resto + une audience.
 * `dryRun` = aperçu (aucun email envoyé, juste le décompte).
 * L'Edge Function garde son propre garde-fou de cadence (min_days) et la
 * désinscription RGPD ; on ne double-blast jamais un client récent.
 */
export async function runRetentionWave(opts: {
  tenantSlug?: string;
  audience: RetentionAudience;
  dryRun: boolean;
  minDays?: number;
}): Promise<WaveResult> {
  const { data, error } = await supabase.functions.invoke('send-retention-email', {
    body: {
      tenant_slug: opts.tenantSlug ?? 'big-burger',
      audience: opts.audience,
      dry_run: opts.dryRun,
      ...(opts.minDays != null ? { min_days: opts.minDays } : {}),
    },
  });
  if (error) return { ok: false, dryRun: opts.dryRun, message: error.message };
  const d = (data ?? {}) as Record<string, unknown>;
  if (!d.ok) return { ok: false, dryRun: opts.dryRun, message: String(d.error ?? 'Échec.') };

  if (opts.dryRun) {
    const toSend = Number(d.to_send ?? 0);
    return {
      ok: true,
      dryRun: true,
      toSend,
      candidates: Number(d.candidates ?? 0),
      excludedRecent: Number(d.excluded_recent ?? 0),
      message: `${toSend} email(s) seraient envoyés (${d.candidates ?? 0} candidats, ${d.excluded_recent ?? 0} exclus car récents).`,
    };
  }
  const sent = Number(d.sent ?? 0);
  const errors = Number(d.errors ?? 0);
  return {
    ok: true,
    dryRun: false,
    sent,
    errors,
    candidates: Number(d.candidates ?? 0),
    excludedRecent: Number(d.excluded_recent ?? 0),
    message: `Vague envoyée : ${sent} email(s), ${errors} erreur(s).`,
  };
}
